import useSettings from '../../hooks/Settings';
import Table from './table/Table';

const TOTAL_KEY = '__total__';

/**
 * Renders a per-project "where the data came from" table for use inside an
 * existing InfoPopover modal.
 *
 * Two data modes:
 *   - `values`: an explicit { [projectName]: number } map — use when the
 *     meaningful number is a summed result value (e.g. output cards).
 *   - `hierarchy`: the object returned by useHierarchyData — keys are project
 *     names, `totalDocs` is the record count per project. Used when the count
 *     of records is itself the answer (e.g. questionnaire responses).
 * `values` takes precedence when both are passed.
 *
 * Renders nothing on a project tenant or when there's no data — safe to drop
 * into any details popover unconditionally.
 *
 * @param {object} props
 * @param {Record<string, { totalDocs?: number }>} [props.hierarchy]
 * @param {Record<string, number|string>} [props.values] - Per-project value (count or average)
 * @param {string} [props.valueLabel] - Header label for the value column
 * @param {boolean} [props.average] - When true the Totaal row is formatted to one decimal (averages); otherwise an integer (counts).
 */
const ProjectsBreakdownSection = ({ hierarchy, values, valueLabel = 'Antwoorden', average = false }) => {
    const portfolio = useSettings()?.portfolio;
    if (portfolio !== 'block') return null;

    // AI-CONTEXT: See docs/pitfalls.md#database-totaldocs-overcounts-respondents
    // `hierarchy` mode shows raw `totalDocs` — for a respondent count pass
    // `respondentsPerProject` via `values` instead (totalDocs over-counts
    // multiple-choice / matrix / paragraph-moment).
    const projectRows = (values
        ? Object.entries(values).map(([name, value]) => ({ name, value: value || 0 }))
        : Object.entries(hierarchy || {}).map(([name, info]) => ({ name, value: info?.totalDocs || 0 }))
    ).sort((a, b) => b.value - a.value);

    if (projectRows.length === 0) return null;
    if (projectRows.every((r) => r.value === 0)) return null;

    // Coerce per-row values to numbers — average values arrive as `toFixed(1)`
    // strings, so `0 + '2.0'` would otherwise concatenate. Count mode sums across
    // projects; average mode shows the mean (summing averages is meaningless).
    const total = projectRows.reduce((sum, r) => sum + (Number(r.value) || 0), 0);
    const totalDisplay = average
        ? (total / projectRows.length).toFixed(1)
        : total;

    const items = [
        ...projectRows.map((r) => ({ ...r, key: r.name, isTotal: false })),
        { name: 'Totaal', value: totalDisplay, key: TOTAL_KEY, isTotal: true },
    ];

    const columns = [
        { key: 'name', label: 'Project', searchable: false },
        { key: 'value', label: valueLabel, searchable: false },
    ];

    return (
        <div className="info-popover-section projects-breakdown-section">
            <p className="info-popover-chart-title">Projecten</p>
            <Table
                columns={columns}
                items={items}
                getItemKey={(item) => item.key}
                showSearch={false}
                emptyMessage="Geen projecten met data."
                getRowClassName={(item) => (item.isTotal ? 'projects-breakdown-section__total-row' : '')}
                renderRow={(item) => (
                    <>
                        <td>{item.name}</td>
                        <td>{item.value}</td>
                    </>
                )}
            />
        </div>
    );
};

export default ProjectsBreakdownSection;
